/**
 * The audit trail.
 *
 * Rendered as sentences rather than as a grid of columns. An audit log is read
 * when something has gone wrong and the reader does not yet know what they are
 * looking for, and "chris disabled the account ann" is understood at a glance
 * in a way that `user.disabled | 019c… | {"username":"ann"}` is not. The raw
 * row is still one disclosure away, because when the sentence is not enough the
 * exact record is the only thing that will do.
 */

import { Fragment, useEffect, useMemo, useState } from 'react'
import type { ReactElement, ReactNode } from 'react'

import { useAdminUsers, useAuditLog } from '@/api/queries'
import type { AuditFilters } from '@/api/queries'
import type { AuditEvent } from '@/api/types'
import { Button, EmptyState, Select, Spinner, TextField } from '@/components/ui'
import { formatRelative } from '@/lib/format'

import { AdminPage, Card, ErrorNotice, readText } from './AdminLayout'
import styles from './admin.module.css'

/** Offered as suggestions, not as a closed list: a newer server may log more. */
const KNOWN_ACTIONS: readonly string[] = [
  'attachment.deleted',
  'federation.key_rotated',
  'invitation.revoked',
  'member.invited',
  'member.removed',
  'message.deleted',
  'owner.transferred',
  'peer.approved',
  'peer.bootstrap',
  'peer.deleted',
  'peer.requested',
  'peer.revoked',
  'room.created',
  'room.deleted',
  'room.invite.received',
  'room.share.revoked',
  'room.share.updated',
  'room.shared',
  'server.onboarding_completed',
  'server.settings_changed',
  'server.setup_completed',
  'user.created',
  'user.deleted',
  'user.disabled',
  'user.enabled',
  'user.role_changed',
  'webhook.created',
  'webhook.revoked',
  'webhook.rotated',
  'webhook.updated',
]

// ── Sentences ───────────────────────────────────────────────────────────────

function Actor({ event }: { event: AuditEvent }): ReactElement {
  const label = readText(event.actor_label)
  if (label !== null) return <strong>{label}</strong>

  const peer = readText(event.actor_peer_server_id)
  if (peer !== null) return <strong>{peer}</strong>

  return <span>This server</span>
}

function Named({ value, fallback }: { value: string | null; fallback: string }): ReactElement {
  return value === null ? <span>{fallback}</span> : <strong>{value}</strong>
}

function joinList(value: unknown): string | null {
  if (!Array.isArray(value)) return null

  const items = value.flatMap((entry) => {
    const text = readText(entry)
    return text === null ? [] : [text]
  })

  return items.length === 0 ? null : items.join(', ')
}

/**
 * One readable line per action. The default branch is not a fallback so much as
 * a promise: a server that logs something this client has never heard of still
 * produces a legible entry rather than a blank row.
 */
function sentence(event: AuditEvent): ReactNode {
  const actor = <Actor event={event} />
  const field = (key: string): string | null => readText(event.metadata[key])

  switch (event.action) {
    case 'user.created':
      return (
        <>
          {actor} created the account <Named value={field('username')} fallback="a new account" />.
        </>
      )
    case 'user.deleted':
      return (
        <>
          {actor} deleted the account <Named value={field('username')} fallback="an account" />.
        </>
      )
    case 'user.enabled':
      return (
        <>
          {actor} let <Named value={field('username')} fallback="an account" /> sign in again.
        </>
      )
    case 'user.disabled':
      return (
        <>
          {actor} disabled <Named value={field('username')} fallback="an account" /> and signed it
          out everywhere.
        </>
      )
    case 'user.role_changed': {
      const role = field('role')
      return (
        <>
          {actor} changed the role of{' '}
          <Named value={field('username')} fallback="somebody" />
          {role === null ? '' : ` to ${role}`}.
        </>
      )
    }
    case 'owner.transferred':
      return (
        <>
          {actor} transferred ownership of this server from{' '}
          <Named value={field('from')} fallback="the previous owner" /> to{' '}
          <Named value={field('to')} fallback="somebody else" />.
        </>
      )
    case 'member.invited': {
      // The same action name covers adding somebody to a channel and inviting
      // them to the server; the metadata is what tells the two apart.
      const email = field('email')
      const role = field('role')
      const as = role === null ? '' : ` as ${role}`

      return email === null ? (
        <>
          {actor} added <Named value={field('identity')} fallback="somebody" /> to a channel
          {as}.
        </>
      ) : (
        <>
          {actor} invited <strong>{email}</strong> to this server{as}.
        </>
      )
    }
    case 'member.removed':
      return (
        <>
          {actor} removed <Named value={field('identity')} fallback="somebody" /> from a channel.
        </>
      )
    case 'invitation.revoked':
      return (
        <>
          {actor} revoked the invitation to{' '}
          <Named value={field('email')} fallback="an address" />.
        </>
      )
    case 'room.created':
      return (
        <>
          {actor} created the channel <Named value={field('slug')} fallback="a channel" />.
        </>
      )
    case 'room.deleted':
      return (
        <>
          {actor} deleted the channel <Named value={field('slug')} fallback="a channel" />.
        </>
      )
    case 'room.shared':
      return (
        <>
          {actor} shared a channel with{' '}
          <Named value={field('peer_server_id')} fallback="another server" />.
        </>
      )
    case 'room.share.updated':
      return (
        <>
          {actor} changed what{' '}
          <Named value={field('peer_server_id')} fallback="another server" /> may do in a shared
          channel.
        </>
      )
    case 'room.share.revoked':
      return (
        <>
          {actor} stopped sharing a channel with{' '}
          <Named value={field('peer_server_id')} fallback="another server" />.
        </>
      )
    case 'room.share.accepted':
      return (
        <>
          <Named value={field('peer_server_id')} fallback="Another server" /> accepted a shared
          channel.
        </>
      )
    case 'room.share.rejected':
      return (
        <>
          <Named value={field('peer_server_id')} fallback="Another server" /> declined a shared
          channel.
        </>
      )
    case 'room.invite.received':
      return (
        <>
          <Named value={field('peer_server_id')} fallback="Another server" /> offered to share a
          channel with this one.
        </>
      )
    case 'message.deleted':
      return <>{actor} deleted somebody else&apos;s message.</>
    case 'attachment.deleted':
      return (
        <>
          {actor} deleted the file <Named value={field('filename')} fallback="an attachment" />.
        </>
      )
    case 'webhook.created':
      return (
        <>
          {actor} created the webhook <Named value={field('name')} fallback="a webhook" />.
        </>
      )
    case 'webhook.updated':
      return <>{actor} changed a webhook.</>
    case 'webhook.rotated':
      return <>{actor} issued a new secret for a webhook, invalidating the old one.</>
    case 'webhook.revoked':
      return (
        <>
          {actor} deleted the webhook <Named value={field('name')} fallback="a webhook" />.
        </>
      )
    case 'peer.bootstrap':
      return (
        <>
          {actor} generated a connection key for{' '}
          <Named value={field('server_id')} fallback="another server" />.
        </>
      )
    case 'peer.requested':
      return (
        <>
          {actor} started pairing with{' '}
          <Named value={field('server_id')} fallback="another server" />.
        </>
      )
    case 'peer.approved':
      return (
        <>
          {actor} approved <Named value={field('server_id')} fallback="another server" /> after
          checking its fingerprint.
        </>
      )
    case 'peer.revoked': {
      const reason = field('reason')
      return (
        <>
          {actor} revoked <Named value={field('server_id')} fallback="another server" />
          {reason === null ? '' : ` — ${reason}`}.
        </>
      )
    }
    case 'peer.deleted':
      return (
        <>
          {actor} removed <Named value={field('server_id')} fallback="another server" /> and forgot
          its key.
        </>
      )
    case 'federation.key_rotated':
      return (
        <>
          {actor} rotated this server&apos;s federation identity key. Every peer has to verify the
          new fingerprint.
        </>
      )
    case 'server.settings_changed': {
      const changed = joinList(event.metadata['changed_keys'])
      return (
        <>
          {actor} changed the server settings
          {changed === null ? '' : `: ${changed}`}.
        </>
      )
    }
    case 'server.setup_completed':
      return <>{actor} finished setting this server up.</>
    case 'server.onboarding_completed':
      return <>{actor} finished the first-run wizard.</>
    default:
      return (
        <>
          {actor} performed <strong>{event.action}</strong>
          {event.target_type === null ? '' : ` on a ${event.target_type}`}.
        </>
      )
  }
}

function stringify(value: unknown): string {
  if (typeof value === 'string') return value
  try {
    return JSON.stringify(value) ?? 'null'
  } catch {
    return String(value)
  }
}

// ── The page ────────────────────────────────────────────────────────────────

export function AuditPage(): ReactElement {
  const [actionInput, setActionInput] = useState('')
  const [action, setAction] = useState('')
  const [actorId, setActorId] = useState('')

  useEffect(() => {
    const timer = window.setTimeout(() => setAction(actionInput.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [actionInput])

  const filters = useMemo<AuditFilters>(() => {
    const next: AuditFilters = {}
    if (action !== '') next.action = action
    if (actorId !== '') next.actor_user_id = actorId
    return next
  }, [action, actorId])

  const log = useAuditLog(filters)
  const users = useAdminUsers()

  const entries = log.data ?? []
  const filtered = action !== '' || actorId !== ''

  const clear = (): void => {
    setActionInput('')
    setAction('')
    setActorId('')
  }

  return (
    <AdminPage
      title="Audit"
      lead="What administrators and connected servers have done here. The trail is written by the server as it acts and cannot be edited from these pages."
    >
      <Card>
        <div className={styles.filters}>
          <TextField
            className={styles.grow}
            label="Action"
            list="audit-actions"
            value={actionInput}
            onChange={(event) => setActionInput(event.target.value)}
            placeholder="user.disabled"
            hint="An exact action name. Start typing to see the ones this client knows."
            autoComplete="off"
            spellCheck={false}
          />
          <datalist id="audit-actions">
            {KNOWN_ACTIONS.map((name) => (
              <option value={name} key={name} />
            ))}
          </datalist>

          <Select
            label="Done by"
            value={actorId}
            onChange={(event) => setActorId(event.target.value)}
            options={[
              { value: '', label: 'Anybody' },
              ...(users.data ?? []).map((user) => ({
                value: user.id,
                label: `${user.display_name} (${user.username})`,
              })),
            ]}
            disabled={users.isPending}
          />

          <Button onClick={clear} disabled={!filtered}>
            Clear filters
          </Button>
        </div>
      </Card>

      <Card>
        {log.isError ? (
          <ErrorNotice error={log.error} title="The audit trail could not be read">
            <Button onClick={() => void log.refetch()}>Try again</Button>
          </ErrorNotice>
        ) : log.isPending ? (
          <Spinner size="lg" label="Reading the audit trail" />
        ) : entries.length === 0 ? (
          <EmptyState
            title="Nothing recorded"
            description={
              filtered
                ? 'No entry matches these filters. An action name has to match exactly.'
                : 'Nothing has been done on this server that is worth recording yet.'
            }
          />
        ) : (
          <>
            <ol className={styles.entries}>
              {entries.map((event) => (
                <li className={styles.entry} key={event.id}>
                  <p className={styles.entrySentence}>{sentence(event)}</p>
                  <p className={styles.entryMeta}>
                    <time dateTime={event.created_at} title={event.created_at}>
                      {formatRelative(event.created_at)}
                    </time>
                    <span className={styles.mono}>{event.action}</span>
                    {event.target_type === null ? null : (
                      <span>
                        {event.target_type}
                        {event.target_id === null ? '' : ` ${event.target_id}`}
                      </span>
                    )}
                  </p>
                  {Object.keys(event.metadata).length === 0 ? null : (
                    <details className={styles.entryDetails}>
                      <summary className={styles.entrySummary}>Recorded detail</summary>
                      <dl className={styles.metaGrid}>
                        {Object.entries(event.metadata).map(([key, value]) => (
                          <Fragment key={key}>
                            <dt className={styles.factTerm}>{key}</dt>
                            <dd className={styles.factValue}>
                              <span className={styles.mono}>{stringify(value)}</span>
                            </dd>
                          </Fragment>
                        ))}
                      </dl>
                    </details>
                  )}
                </li>
              ))}
            </ol>

            <div className={styles.loadMore}>
              {log.hasNextPage === true ? (
                <Button
                  loading={log.isFetchingNextPage}
                  onClick={() => void log.fetchNextPage()}
                >
                  Load older entries
                </Button>
              ) : (
                <p className={styles.small}>That is the whole trail.</p>
              )}
            </div>
          </>
        )}
      </Card>
    </AdminPage>
  )
}
